import { useState } from 'react';
import { Modal, Input, TextArea } from '../../../shared/components';
import Button from '../../../shared/components/Button';

interface NameDescriptionModalProps {
  title: string;
  /** Absent = create; present = edit. */
  initial?: { name: string; description: string | null };
  isPending: boolean;
  onSubmit: (values: { name: string; description?: string }) => Promise<unknown>;
  onClose: () => void;
}

/**
 * PE-2147 — shared create/edit form for a department or a department role:
 * both are `{ name (2–255), description? (≤1000) }` server-side.
 */
export const NameDescriptionModal = ({ title, initial, isPending, onSubmit, onClose }: NameDescriptionModalProps) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    setError(undefined);
    await onSubmit({ name: trimmed, description: description.trim() || undefined });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="sm">
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        <div>
          <Input
            name="name"
            label="Name"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Legal"
            className="h-[38px]"
          />
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
        <TextArea
          name="description"
          label="Description (optional)"
          value={description}
          maxLength={1000}
          onChange={(e) => setDescription(e.target.value)}
          className="bg-muted rounded-[10px] h-[90px] w-full"
        />
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending} loading={isPending}>
            {initial ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
